import { useEffect, useState } from "react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../utils/api";
import ImageCropper from "./ImageCropper";

export default function TeamManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState(null); // {id, name, position, image_filename} oder null

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const reload = async () => {
    setLoading(true);
    const { data } = await api.getPublicTeam();
    setLoading(false);
    if (data && data.ok) setItems(data.items || []);
  };

  useEffect(() => { reload(); }, []);

  const startNew = () => setEditing({ id: null, name: "", position: "", image_filename: "" });
  const startEdit = (it) => setEditing({ ...it });
  const cancel = () => { setEditing(null); setError(""); setMessage(""); };

  const save = async (item) => {
    setError(""); setMessage("");
    const payload = {
      name: item.name.trim(),
      position: item.position.trim(),
      image_filename: item.image_filename || "",
    };
    if (!payload.name) { setError("Bitte einen Namen angeben."); return; }
    const { data } = item.id
      ? await api.updateTeam(item.id, payload)
      : await api.createTeam(payload);
    if (data && data.ok) {
      setMessage(item.id ? "Mitarbeiter aktualisiert." : "Mitarbeiter angelegt.");
      setEditing(null);
      await reload();
    } else {
      setError((data && data.message) || "Speichern fehlgeschlagen.");
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Mitarbeiter „${it.name}" wirklich löschen?`)) return;
    const { data } = await api.deleteTeam(it.id);
    if (data && data.ok) { setMessage("Mitarbeiter gelöscht."); await reload(); }
  };

  const onDragEnd = async (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((i) => i.id === active.id);
    const newIdx = items.findIndex((i) => i.id === over.id);
    const nextItems = arrayMove(items, oldIdx, newIdx);
    setItems(nextItems);
    await api.reorderTeam(nextItems.map((i) => i.id));
  };

  if (editing) {
    return (
      <TeamForm
        item={editing}
        onCancel={cancel}
        onSave={save}
        error={error}
        message={message}
      />
    );
  }

  return (
    <div data-testid="team-manager">
      <div className="admin-list-header">
        <h2 className="admin-list-title">{items.length} Mitarbeiter</h2>
        <button type="button" onClick={startNew} className="admin-btn admin-btn-primary" data-testid="team-new">
          + Neuer Mitarbeiter
        </button>
      </div>

      {message && <p className="admin-success">{message}</p>}
      {error && <p className="admin-error">{error}</p>}

      {loading ? <p>Lade…</p> : items.length === 0 ? (
        <p className="admin-empty">Noch keine Mitarbeiter angelegt.</p>
      ) : (
        <>
          <p className="admin-align-hint" style={{ margin: "0 0 0.75rem" }}>
            Die Reihenfolge bestimmt die Anzeige auf der Startseite. Ziehe die Zeilen zum Umsortieren.
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <ul className="team-list" data-testid="team-list">
                {items.map((it) => (
                  <SortableRow key={it.id} item={it} onEdit={() => startEdit(it)} onDelete={() => remove(it)} />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </>
      )}
    </div>
  );
}

function SortableRow({ item, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  return (
    <li ref={setNodeRef} style={style} className="team-list-item" data-testid={`team-item-${item.id}`}>
      <span className="team-drag-handle" {...attributes} {...listeners} title="Ziehen zum Sortieren" data-testid={`team-drag-${item.id}`}>
        <svg width="14" height="20" viewBox="0 0 14 20" fill="currentColor" aria-hidden="true">
          <circle cx="4" cy="4" r="1.5"/><circle cx="10" cy="4" r="1.5"/>
          <circle cx="4" cy="10" r="1.5"/><circle cx="10" cy="10" r="1.5"/>
          <circle cx="4" cy="16" r="1.5"/><circle cx="10" cy="16" r="1.5"/>
        </svg>
      </span>
      <div className="team-thumb" aria-hidden="true">
        {item.image_url ? (
          <img src={item.image_url} alt="" />
        ) : (
          <img src="/team/placeholder.svg" alt="" className="team-thumb-placeholder-img" />
        )}
      </div>
      <div className="team-info">
        <div className="team-name">{item.name}</div>
        <div className="team-position">{item.position || <em>ohne Position</em>}</div>
      </div>
      <div className="team-actions">
        <button type="button" onClick={onEdit} className="admin-btn admin-btn-ghost" data-testid={`team-edit-${item.id}`}>Bearbeiten</button>
        <button type="button" onClick={onDelete} className="admin-btn admin-btn-danger" data-testid={`team-delete-${item.id}`}>Löschen</button>
      </div>
    </li>
  );
}

function TeamForm({ item, onCancel, onSave, error, message }) {
  const [current, setCurrent] = useState({ ...item });
  const [cropSrc, setCropSrc] = useState(null); // Data-URL für Cropper
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { alert("Datei zu groß (max. 8 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const onCropped = async (dataUrl) => {
    setCropSrc(null);
    setUploading(true);
    const { data } = await api.uploadTeamImage(dataUrl);
    setUploading(false);
    if (data && data.ok) {
      setCurrent((c) => ({ ...c, image_filename: data.filename, image_url: data.url }));
    } else {
      alert("Bild-Upload fehlgeschlagen: " + (data?.message || "unbekannter Fehler"));
    }
  };

  const doSave = async () => {
    setSaving(true);
    await onSave(current);
    setSaving(false);
  };

  const imageUrl = current.image_url
    || (current.image_filename ? `/uploads/team/${current.image_filename}` : "");

  return (
    <div className="admin-form" data-testid="team-form">
      <h2 className="admin-list-title">{item.id ? "Mitarbeiter bearbeiten" : "Neuen Mitarbeiter anlegen"}</h2>
      {message && <p className="admin-success">{message}</p>}
      {error && <p className="admin-error">{error}</p>}

      <label className="admin-label">
        Name
        <input
          type="text"
          value={current.name}
          onChange={(e) => setCurrent({ ...current, name: e.target.value })}
          maxLength={100}
          required
          data-testid="team-name-input"
        />
      </label>
      <label className="admin-label">
        Position / Bezeichnung
        <input
          type="text"
          value={current.position}
          onChange={(e) => setCurrent({ ...current, position: e.target.value })}
          maxLength={150}
          placeholder="z. B. Medizinische Fachangestellte"
          data-testid="team-position-input"
        />
      </label>

      <label className="admin-label">
        Foto
        <div className="team-photo-row">
          <div className="team-photo-preview">
            {imageUrl ? (
              <img src={imageUrl} alt="Aktuelles Foto" />
            ) : (
              <img src="/team/placeholder.svg" alt="Platzhalter" className="team-thumb-placeholder-img" />
            )}
          </div>
          <div className="team-photo-actions">
            <label className="admin-btn admin-btn-ghost" style={{ cursor: "pointer" }}>
              {uploading ? "Wird verarbeitet…" : "Foto auswählen…"}
              <input type="file" accept="image/*" onChange={onFile} hidden data-testid="team-file-input" disabled={uploading}/>
            </label>
            <span className="admin-align-hint" style={{ marginTop: "0.5rem" }}>
              Nach der Auswahl kannst du den Zuschnitt anpassen (Zoom + Verschieben).
            </span>
          </div>
        </div>
      </label>

      <div className="admin-actions-row">
        <button type="button" onClick={doSave} className="admin-btn admin-btn-primary" disabled={saving} data-testid="team-save">
          {saving ? "Speichere…" : (item.id ? "Änderungen speichern" : "Anlegen")}
        </button>
        <button type="button" onClick={onCancel} className="admin-btn admin-btn-ghost" data-testid="team-cancel">
          Abbrechen
        </button>
      </div>

      {cropSrc && (
        <ImageCropper imageSrc={cropSrc} onCancel={() => setCropSrc(null)} onCropped={onCropped} />
      )}
    </div>
  );
}
